if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.touchStartX = null;
        this.touchStartY = null;
        this.isPinching = false;
        this.pinch = null; // { img, touchIdA, touchIdB, startDistance, startScale, startMidX, startMidY, startTranslateX, startTranslateY }

        // iOS Safari (WebKit) pinch'i touchmove ile değil, kendi gesturestart/gesturechange
        // olaylarıyla yönetiyor ve touch-action:none bunu güvenilir şekilde engellemiyor.
        // Bu yüzden Safari'de ayrı, otoriter bir yol (event.scale) kullanıyoruz.
        this.supportsGestureEvents = 'ongesturestart' in window;
        this.gestureImg = null;
        this.gestureBaseScale = 1;
        this.gestureBaseTranslateX = 0;
        this.gestureBaseTranslateY = 0;

        const prevButton = this.querySelector('.product-media-modal__nav--prev');
        const nextButton = this.querySelector('.product-media-modal__nav--next');
        if (prevButton) prevButton.addEventListener('click', () => this.showAdjacentMedia(-1));
        if (nextButton) nextButton.addEventListener('click', () => this.showAdjacentMedia(1));

        const content = this.querySelector('.product-media-modal__content');
        this.content = content;
        if (!content) return;

        content.addEventListener(
          'touchstart',
          (event) => {
            if (!event.touches) return;

            if (event.touches.length >= 2) {
              this.touchStartX = null;
              this.touchStartY = null;
              if (!this.supportsGestureEvents) this.beginPinch(event.touches);
              return;
            }

            if (event.touches.length === 1 && !this.isPinching) {
              this.touchStartX = event.touches[0].clientX;
              this.touchStartY = event.touches[0].clientY;
            }
          },
          { passive: true }
        );

        content.addEventListener(
          'touchmove',
          (event) => {
            if (this.supportsGestureEvents) return;
            if (!this.isPinching || !event.touches || event.touches.length < 2) return;
            event.preventDefault();
            this.updatePinch(event.touches);
          },
          { passive: false }
        );

        content.addEventListener(
          'touchend',
          (event) => {
            if (this.isPinching && !this.supportsGestureEvents) {
              this.endPinch(event.touches);
              return;
            }
            if (this.isPinching) return;

            if (this.touchStartX === null || !event.changedTouches || event.changedTouches.length !== 1) return;

            const activeImg = this.getActiveImage();
            if (activeImg && activeImg._pinchState && activeImg._pinchState.scale > 1.02) {
              this.touchStartX = null;
              this.touchStartY = null;
              return;
            }

            const deltaX = event.changedTouches[0].clientX - this.touchStartX;
            const deltaY = event.changedTouches[0].clientY - this.touchStartY;
            this.touchStartX = null;
            this.touchStartY = null;

            const SWIPE_THRESHOLD = 40;
            if (Math.abs(deltaX) < SWIPE_THRESHOLD || Math.abs(deltaX) < Math.abs(deltaY)) return;
            this.showAdjacentMedia(deltaX < 0 ? 1 : -1);
          },
          { passive: true }
        );

        content.addEventListener('touchcancel', () => {
          this.isPinching = false;
          this.pinch = null;
          this.touchStartX = null;
          this.touchStartY = null;
        });

        // ===== Safari/WebKit gesture events (iOS pinch-zoom'un asıl güvenilir yolu) =====
        content.addEventListener('gesturestart', (event) => {
          event.preventDefault();
          const img = this.getActiveImage();
          if (!img) return;
          this.isPinching = true;
          this.touchStartX = null;
          this.touchStartY = null;
          this.gestureImg = img;
          const current = img._pinchState || { scale: 1, translateX: 0, translateY: 0 };
          this.gestureBaseScale = current.scale;
          this.gestureBaseTranslateX = current.translateX;
          this.gestureBaseTranslateY = current.translateY;
          this.setOriginFromPoint(img, event.clientX, event.clientY);
          img.style.transition = 'none';
        });

        content.addEventListener('gesturechange', (event) => {
          event.preventDefault();
          if (!this.gestureImg) return;
          const MIN_SCALE = 1;
          const MAX_SCALE = 3;
          const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.gestureBaseScale * event.scale));
          const bounds = this.clampTranslate(this.gestureImg, scale, this.gestureBaseTranslateX, this.gestureBaseTranslateY);
          this.applyTransform(this.gestureImg, scale, bounds.x, bounds.y);
        });

        content.addEventListener('gestureend', (event) => {
          event.preventDefault();
          this.isPinching = false;
          const img = this.gestureImg;
          this.gestureImg = null;
          if (!img) return;
          const state = img._pinchState || { scale: 1 };
          if (state.scale <= 1.02) {
            this.resetImageZoom(img);
          } else {
            img.style.transition = 'none';
          }
        });
      }

      // Pinch başladığında parmakların tam olarak nerede olduğunu görselin kendi kutusuna
      // göre yüzdeye çevirip transform-origin'i oraya taşır — böylece büyütme her zaman
      // görselin merkezine değil, parmakların bulunduğu noktaya odaklanır.
      setOriginFromPoint(img, clientX, clientY) {
        if (typeof clientX !== 'number' || typeof clientY !== 'number') return;
        const rect = img.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        let originX = ((clientX - rect.left) / rect.width) * 100;
        let originY = ((clientY - rect.top) / rect.height) * 100;
        originX = Math.min(100, Math.max(0, originX));
        originY = Math.min(100, Math.max(0, originY));
        img.style.transformOrigin = originX + '% ' + originY + '%';
      }

      getActiveImage() {
        // Görsel medya için data-media-id doğrudan <img> üzerinde (wrapper yok);
        // video/model için ise <deferred-media>/<product-model> sarmalayıcısı içinde <img> (poster) var.
        const activeMedia = this.querySelector('.product-media-modal__content > .active');
        if (!activeMedia) return null;
        if (activeMedia.tagName === 'IMG') return activeMedia;
        return activeMedia.querySelector('img');
      }

      // Parmak ID'siyle takip eder — dokunma listesindeki sıralama (touches[0]/[1])
      // her olayda aynı fiziksel parmağa denk gelmeyebiliyor, bu sıçramalara yol açıyordu.
      findTouchesByIds(touchList, idA, idB) {
        var a = null;
        var b = null;
        for (var i = 0; i < touchList.length; i++) {
          if (touchList[i].identifier === idA) a = touchList[i];
          if (touchList[i].identifier === idB) b = touchList[i];
        }
        return [a, b];
      }

      touchDistance(t1, t2) {
        const dx = t1.clientX - t2.clientX;
        const dy = t1.clientY - t2.clientY;
        return Math.sqrt(dx * dx + dy * dy);
      }

      touchMidpoint(t1, t2) {
        return { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
      }

      beginPinch(touches) {
        const img = this.getActiveImage();
        if (!img) return;

        const t1 = touches[0];
        const t2 = touches[1];
        const mid = this.touchMidpoint(t1, t2);
        const current = img._pinchState || { scale: 1, translateX: 0, translateY: 0 };

        this.isPinching = true;
        this.pinch = {
          img: img,
          touchIdA: t1.identifier,
          touchIdB: t2.identifier,
          startDistance: this.touchDistance(t1, t2),
          startScale: current.scale,
          startTranslateX: current.translateX,
          startTranslateY: current.translateY,
          startMidX: mid.x,
          startMidY: mid.y,
        };
        this.setOriginFromPoint(img, mid.x, mid.y);
        img.style.transition = 'none';
      }

      updatePinch(touches) {
        if (!this.pinch || !this.pinch.img) return;
        const p = this.pinch;
        const found = this.findTouchesByIds(touches, p.touchIdA, p.touchIdB);
        if (!found[0] || !found[1]) return;

        const distance = this.touchDistance(found[0], found[1]);
        const mid = this.touchMidpoint(found[0], found[1]);

        const MIN_SCALE = 1;
        const MAX_SCALE = 3;
        let scale = p.startScale * (distance / p.startDistance);
        scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

        let translateX = p.startTranslateX + (mid.x - p.startMidX);
        let translateY = p.startTranslateY + (mid.y - p.startMidY);

        var bounds = this.clampTranslate(p.img, scale, translateX, translateY);
        this.applyTransform(p.img, scale, bounds.x, bounds.y);
      }

      endPinch(remainingTouches) {
        if (!this.pinch || !this.pinch.img) {
          this.isPinching = false;
          this.pinch = null;
          return;
        }
        const img = this.pinch.img;

        if (remainingTouches && remainingTouches.length >= 2) {
          // Hâlâ 2+ parmak varsa (üçüncü parmak vb.) mevcut ikiliyle devam et
          return;
        }

        this.isPinching = false;
        this.pinch = null;
        this.touchStartX = null;
        this.touchStartY = null;

        const state = img._pinchState || { scale: 1, translateX: 0, translateY: 0 };
        if (state.scale <= 1.02) {
          this.resetImageZoom(img);
        } else {
          img.style.transition = 'none';
        }
      }

      clampTranslate(img, scale, translateX, translateY) {
        if (scale <= 1) return { x: 0, y: 0 };
        const rect = img.getBoundingClientRect();
        const currentScale = (img._pinchState && img._pinchState.scale) || 1;
        const baseWidth = rect.width / currentScale;
        const baseHeight = rect.height / currentScale;
        const maxX = (baseWidth * (scale - 1)) / 2;
        const maxY = (baseHeight * (scale - 1)) / 2;
        return {
          x: Math.min(maxX, Math.max(-maxX, translateX)),
          y: Math.min(maxY, Math.max(-maxY, translateY)),
        };
      }

      applyTransform(img, scale, translateX, translateY) {
        // transform-origin buraya yazılmıyor: setOriginFromPoint() gesture başında bir kere
        // ayarlıyor, her karede sıfırlarsak zoom her seferinde merkeze geri kayardı.
        img._pinchState = { scale: scale, translateX: translateX, translateY: translateY };
        img.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')';
      }

      resetImageZoom(img) {
        if (!img) return;
        img.style.transition = 'transform 0.2s ease';
        img.style.transform = '';
        img.style.transformOrigin = '';
        img._pinchState = { scale: 1, translateX: 0, translateY: 0 };
      }

      resetAllZoom() {
        if (!this.content) return;
        this.content.querySelectorAll('img').forEach((img) => this.resetImageZoom(img));
        this.isPinching = false;
        this.pinch = null;
        this.gestureImg = null;
      }

      hide() {
        this.resetAllZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.activateMedia(this.openedBy.getAttribute('data-media-id'));
      }

      activateMedia(mediaId) {
        this.resetAllZoom();

        this.querySelectorAll(`[data-media-id]:not([data-media-id="${mediaId}"])`).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      showAdjacentMedia(direction) {
        const mediaItems = Array.from(this.querySelectorAll('[data-media-id]'));
        if (mediaItems.length < 2) return;

        const currentIndex = mediaItems.findIndex((element) => element.classList.contains('active'));
        if (currentIndex === -1) return;

        let nextIndex = (currentIndex + direction) % mediaItems.length;
        if (nextIndex < 0) nextIndex += mediaItems.length;

        this.activateMedia(mediaItems[nextIndex].getAttribute('data-media-id'));
      }
    }
  );
}
